import maplibregl, { Map, Marker } from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { useEffect, useRef, useState } from 'react'
import locator from '../images/locator.svg'
import locked from '../images/locked.svg'
import north from '../images/north.svg'
import tent from '../images/tent.svg'
import unlocked from '../images/unlocked.svg'

import exit from '../images/exit.svg'
import floors from '../images/floors.svg'
import water from '../images/water.svg'
import wc from '../images/wc.svg'
import noborder from '../images/no-border.svg'
import bikeAllowed from '../images/bike-allowed.svg'
import bikeProhibited from '../images/bike-prohibited.svg'
import noborderBar from '../images/barrierefrei-bar.svg'
import noborderCamp from '../images/barrierefrei-camp.svg'
import noborderDancefloor from '../images/barrierefrei-dancefloor.svg'
import noborderToilet from '../images/barrierefrei-wc.svg'
import wegBefestigt from '../images/weg-befestigt.png'
import wegSemiBefestigt from '../images/weg-semi-befestigt.png'

import { Body1, Body2, Message, T } from '../components'
import { useT } from '../state/language'
import { MapButton } from './components/MapButton'
import {
  LegendItem,
  LegendModal,
  LegendContent,
} from './components/LegendModal'
import {
  FUSION_CENTER,
  MAX_NE,
  MAX_SW,
  NE,
  SW,
  TENT_ICON_KEY,
  TENT_INITIAL_POSITION,
  TENT_LOCKED_KEY,
} from './constants/map'
import { waitMs } from './helpers/map'
import { addLayers, EXITS, WATER, FLOORS, WC, ACCESSIBILITY } from './layers'
import { ALL_LAYERS, FsnLayerId } from './models/map'
import { createTent } from './tent'
import {
  ButtonsContainer,
  MapContainer,
  InfoContainer,
  MessageContainer,
  MapDivider,
  FlexColumn,
  ImgContainer,
} from './Map.styled'
import {
  FullWidthButton,
  LegendButtonContainer,
} from './components/LegendButton'
import { useNavigate, useParams } from 'react-router-dom'

const geolocator = new maplibregl.GeolocateControl({
  positionOptions: {
    enableHighAccuracy: true,
  },
  trackUserLocation: true,
})

const MAX_ZOOM = 15.6

export function MapComponent() {
  const t = useT()
  const navigate = useNavigate()

  const params = useParams()
  const showNoBorder =
    params.filter === 'no-border' || params.filter === 'noborder'

  const dragMeText = t('TENT_DRAG_ME')
  const lockMeText = t('TENT_LOCK_ME')

  const mapContainer = useRef(null)

  const [userLocation, setUserLocation] = useState<{
    longitude: number
    latitude: number
  } | null>(null)

  const [tentMarker, setTentMarker] = useState<Marker | null>(null)
  const [tentLocked, setTentLocked] = useState<boolean>(false)
  const [layersModalOpen, setLayersModalOpen] = useState<boolean>(false)
  const [locationDenied, setLocationDenied] = useState<boolean>(false)
  const [theMap, setTheMap] = useState<Map | null>(null)
  const [bearing, setBearing] = useState<number>(0)

  const [layersViz, setLayersViz] = useState(() => {
    const initialViz = ALL_LAYERS.reduce((a, layer) => {
      a[layer] = 'visible'

      return a
    }, {})

    return initialViz
  })

  useEffect(() => {
    if (!theMap) return

    const newLayersViz = { ...layersViz }

    if (showNoBorder) {
      newLayersViz[ACCESSIBILITY] = 'visible'
    } else {
      newLayersViz[ACCESSIBILITY] = 'none'
    }

    // Update the map layers
    Object.entries(newLayersViz).forEach(([layerId, visibility]) => {
      theMap.setLayoutProperty(
        layerId,
        'visibility',
        visibility as 'visible' | 'none',
      )
    })

    // Update the state
    setLayersViz(newLayersViz)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNoBorder])

  useEffect(() => {
    const tentLockedString = localStorage.getItem(TENT_LOCKED_KEY)

    if (tentLockedString) {
      setTentLocked(JSON.parse(tentLockedString))
    }
  }, [])

  useEffect(() => {
    const highlightMarker = async () => {
      if (tentMarker) {
        tentMarker.setRotation(20)
        await waitMs(100)
        tentMarker.setRotation(0)

        if (tentLocked) {
          tentMarker.addClassName('locked')
          const popup = tentMarker.getPopup()
          if (popup?.isOpen()) {
            tentMarker.togglePopup()
          }
        } else {
          tentMarker.removeClassName('locked')
        }
      }
    }

    if (tentMarker) {
      tentMarker.setDraggable(!tentLocked)
      highlightMarker()
    }
  }, [tentLocked, tentMarker])

  useEffect(() => {
    const map = new maplibregl.Map({
      container: mapContainer.current!,
      style: { version: 8, sources: {}, layers: [] },
      center: FUSION_CENTER,
      maxBounds: [MAX_SW, MAX_NE],
      zoom: 13,
      minZoom: 12.8,
      maxZoom: MAX_ZOOM,
      pitchWithRotate: false,
      touchPitch: false,
    })
    map.addControl(geolocator)

    map.on('load', () => {
      setTheMap(map)
    })

    map.on('rotate', () => {
      setBearing(map.getBearing())
    })

    geolocator.once('geolocate', () => {
      map.fitBounds([SW, NE])
    })

    geolocator.on('geolocate', (data) => {
      if (data?.coords) {
        setUserLocation(data.coords)
      }
    })

    return () => {
      if (map) {
        map.removeControl(geolocator)
        map.remove()
      }
    }
  }, [])

  useEffect(() => {
    if (!theMap) return

    theMap.fitBounds([SW, NE])

    addLayers(theMap, showNoBorder)

    const theTent: Marker = createTent(theMap, dragMeText, lockMeText)

    setTentMarker(theTent)

    geolocator.trigger()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theMap, dragMeText, lockMeText])

  const centerMe = () => {
    if (locationDenied) {
      return
    }

    if (!userLocation) {
      geolocator.trigger()
      return
    }

    theMap!.flyTo({
      center: [userLocation.longitude, userLocation.latitude],
      zoom: MAX_ZOOM,
      speed: 2,
      curve: 1.42,
    })
  }

  const findMyTent = () => {
    const tentLocationState = localStorage.getItem(TENT_ICON_KEY)
    const tentLocation = tentLocationState
      ? JSON.parse(tentLocationState)
      : { lng: TENT_INITIAL_POSITION[0], lat: TENT_INITIAL_POSITION[1] }

    theMap!.flyTo({
      center: [tentLocation.lng, tentLocation.lat],
      zoom: MAX_ZOOM,
      speed: 2,
      curve: 1.42,
    })
  }

  const toggleTentLock = () => {
    setTentLocked(!tentLocked)
    localStorage.setItem(TENT_LOCKED_KEY, String(!tentLocked))
  }

  const resetNorth = () => {
    if (!theMap) return
    theMap?.resetNorth()
  }

  const toggleLayerViz = (layerId: FsnLayerId) => {
    if (!theMap) return

    const visibility = layersViz[layerId] === 'visible' ? 'none' : 'visible'

    theMap.setLayoutProperty(layerId, 'visibility', visibility)

    setLayersViz({
      ...layersViz,
      [layerId]: visibility,
    })
  }

  const locationGranted = () => {
    return
  }

  const locationGrantError = (err) => {
    if (err.code === 1) {
      setLocationDenied(true)
    }
  }

  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      locationGranted,
      locationGrantError,
    )
  }

  return (
    <>
      <LegendButtonContainer>
        <FullWidthButton
          onClick={() => {
            setLayersModalOpen(true)
          }}
        >
          <T id="LAYERS_MODAL_BUTTON_CLOSED" />
        </FullWidthButton>
      </LegendButtonContainer>

      <ButtonsContainer $bottom>
        <MapButton
          isActive={!!userLocation}
          onClick={centerMe}
          iconSrc={locator}
        />
        <MapButton
          onClick={resetNorth}
          isActive={bearing !== 0}
          bearing={bearing}
          iconSrc={north}
        />
        <MapButton isActive={true} onClick={findMyTent} iconSrc={tent} />
        <MapButton
          isActive={tentLocked}
          onClick={toggleTentLock}
          iconSrc={tentLocked ? locked : unlocked}
        />

        <MapButton
          onClick={() => navigate(showNoBorder ? '/map' : '/map/no-border')}
          isActive={layersViz[ACCESSIBILITY] === 'visible'}
          iconSrc={noborder}
        />
      </ButtonsContainer>

      {locationDenied && (
        <InfoContainer>
          <MessageContainer>
            <Message id="LOCATION_DENIED" />
          </MessageContainer>
        </InfoContainer>
      )}

      <MapContainer ref={mapContainer} />

      <LegendModal
        isOpen={layersModalOpen}
        onClose={() => setLayersModalOpen(false)}
      >
        {!showNoBorder ? (
          <LegendContent>
            <LegendItem onClick={() => toggleLayerViz(FLOORS)}>
              <MapButton
                isActive={layersViz[FLOORS] === 'visible'}
                iconSrc={floors}
              />
              <Body1>
                <T
                  id={
                    layersViz[FLOORS] === 'visible'
                      ? 'HIDE_FLOORS'
                      : 'SHOW_FLOORS'
                  }
                />
              </Body1>
            </LegendItem>

            <LegendItem onClick={() => toggleLayerViz(WC)}>
              <MapButton isActive={layersViz[WC] === 'visible'} iconSrc={wc} />
              <Body1>
                <T id={layersViz[WC] === 'visible' ? 'HIDE_WC' : 'SHOW_WC'} />
              </Body1>
            </LegendItem>

            <LegendItem onClick={() => toggleLayerViz(WATER)}>
              <MapButton
                isActive={layersViz[WATER] === 'visible'}
                iconSrc={water}
              />
              <Body1>
                <T
                  id={
                    layersViz[WATER] === 'visible' ? 'HIDE_WATER' : 'SHOW_WATER'
                  }
                />
              </Body1>
            </LegendItem>

            <LegendItem onClick={() => toggleLayerViz(EXITS)}>
              <MapButton
                isActive={layersViz[EXITS] === 'visible'}
                iconSrc={exit}
              />
              <Body1>
                <T
                  id={
                    layersViz[EXITS] === 'visible' ? 'HIDE_EXITS' : 'SHOW_EXITS'
                  }
                />
              </Body1>
            </LegendItem>

            <MapDivider />

            <LegendItem>
              <img alt={''} src={bikeAllowed} />
              <FlexColumn>
                <Body1>
                  <T id={'BIKE_ALLOWED'} />
                </Body1>
                <Body2>
                  <T id={'BIKE_ALLOWED_DESC'} />
                </Body2>
              </FlexColumn>
            </LegendItem>
            <LegendItem>
              <img alt={''} src={bikeProhibited} />
              <FlexColumn>
                <Body1>
                  <T id={'BIKE_PROHIBITED'} />
                </Body1>
                <Body2>
                  <T id={'BIKE_PROHIBITED_DESC'} />
                </Body2>
              </FlexColumn>
            </LegendItem>
          </LegendContent>
        ) : (
          <LegendContent>
            <LegendItem>
              <ImgContainer>
                <img alt={''} src={noborderDancefloor} className="icon" />
              </ImgContainer>
              <FlexColumn>
                <Body1>
                  <T id={'BARRIER_FREE_FLOOR'} />
                </Body1>
                <Body2>
                  <T id={'BARRIER_FREE_FLOOR_DESC'} />
                </Body2>
              </FlexColumn>
            </LegendItem>

            <LegendItem>
              <ImgContainer>
                <img alt={''} src={noborderBar} className="icon" />
              </ImgContainer>
              <FlexColumn>
                <Body1>
                  <T id={'BARRIER_FREE_BAR'} />
                </Body1>
                <Body2>
                  <T id={'BARRIER_FREE_BAR_DESC'} />
                </Body2>
              </FlexColumn>
            </LegendItem>

            <LegendItem>
              <ImgContainer>
                <img alt={''} src={noborderCamp} className="icon" />
              </ImgContainer>
              <Body1>
                <T id={'BARRIER_FREE_CAMP'} />
              </Body1>
            </LegendItem>

            <LegendItem>
              <ImgContainer>
                <img alt={''} src={noborderToilet} className="icon" />
              </ImgContainer>
              <Body1>
                <T id={'BARRIER_FREE_WC'} />
              </Body1>
            </LegendItem>
            <MapDivider />

            <LegendItem>
              <ImgContainer>
                <img alt={''} src={wegBefestigt} />
              </ImgContainer>
              <Body1>
                <T id={'FIXED_PATH'} />
              </Body1>
            </LegendItem>
            <LegendItem>
              <ImgContainer>
                <img alt={''} src={wegSemiBefestigt} />
              </ImgContainer>
              <FlexColumn>
                <Body1>
                  <T id={'SEMI_FIXED_PATH'} />
                </Body1>
                <Body2>
                  <T id={'SEMI_FIXED_PATH_DESC'} />
                </Body2>
              </FlexColumn>
            </LegendItem>
          </LegendContent>
        )}
      </LegendModal>
    </>
  )
}
